import * as React from "react";

import classNames from "classnames/bind";
import UIIcon from "common/blocks/UIIcon";
import useDisableScroll from "hooks/useDisableScroll/useDisableScroll";
import type { ResolvedMegaMenuPromo } from "resolvers/base/megaMenuSectionPromoResolver";
import type { ResolvedNavItem } from "resolvers/base/navigationResolver";
import type { ResolvedStockNumberSearch } from "resolvers/base/stockNumberSearchResolver";
import type { ResolvedSubNavItems } from "resolvers/base/subNavigationResolver";
import type { ResolvedMegaMenuItem } from "resolvers/data/header/megaMenuResolver";

import styles from "./HeaderMobileMenu.module.scss";
import { MainMenu, MainMenuProps } from "./MainMenu";
import { MegaMenu, MegaMenuMobileProps } from "./MegaMenu";
import { SubMenu, SubMenuProps } from "./SubMenu";
import type { StepView } from "./interfaces";

type HeaderMobileMenuProps = {
    handleToggleMobileMenu(event: React.MouseEvent<HTMLButtonElement> | Event): void;
    megaMenuItems: ResolvedMegaMenuItem[];
    mobileMenuOpen: boolean;
    mobilePromos: ResolvedMegaMenuPromo[];
    navItems: {
        primary: ResolvedNavItem[];
        secondary: ResolvedNavItem[];
    };
    subMenuItems: ResolvedSubNavItems[];
    stockNumberSearch: ResolvedStockNumberSearch;
    menuHeadings: {
        primary: string;
        secondary: string;
        tertiary: string;
    };
    phoneLinesText?: string;
    isPhoneLinesOpen: boolean;
};

const cx = classNames.bind(styles);

const MENU_COMPONENTS: { [key: string]: any } = {
    0: MainMenu,
    1: SubMenu,
    2: MegaMenu,
};

const INITIAL_STEP_VIEW_DATA: StepView[] = [
    {
        stepIndex: 0,
        stepData: null,
    },
];

const HeaderMobileMenu = ({
    handleToggleMobileMenu,
    megaMenuItems,
    mobileMenuOpen,
    mobilePromos,
    navItems,
    menuHeadings,
    subMenuItems,
    stockNumberSearch,
    phoneLinesText,
    isPhoneLinesOpen,
}: HeaderMobileMenuProps) => {
    const [stepViewData, setStepViewData] = React.useState<StepView[]>([]);
    const menuRef = React.useRef<null | HTMLDivElement>(null);

    const handleNextStepView = (stepView: StepView): void => {
        setStepViewData((current) => [...current, stepView]);
    };

    const handleBackStepView = (): void => {
        setStepViewData((current: StepView[]): StepView[] => {
            const copy = [...current];
            copy.pop();
            return copy;
        });
    };

    const showMobilePromos = false;
    const showSellButton = false;

    const props: { [key: string]: any } = {
        0: {
            menuHeadings,
            navItems,
            handleNextStepView,
            mobilePromos,
            showMobilePromos,
            showSellButton,
            phoneLinesText,
            isPhoneLinesOpen,
        } as MainMenuProps,
        1: {
            handleNextStepView,
            handleBackStepView,
            stepViewData: stepViewData[stepViewData.length - 1],
            subMenuItems,
        } as SubMenuProps,
        2: {
            handleBackStepView,
            megaMenuItems,
            stepViewData: stepViewData[stepViewData.length - 1],
            stockNumberSearch,
            menuRef,
        } as MegaMenuMobileProps,
    };

    React.useEffect(() => {
        setStepViewData(() => (mobileMenuOpen ? INITIAL_STEP_VIEW_DATA : []));

        return () => {
            setStepViewData([]);
        };
    }, [mobileMenuOpen]);

    useDisableScroll(mobileMenuOpen);

    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (
                mobileMenuOpen &&
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                handleToggleMobileMenu(event);
                setStepViewData(INITIAL_STEP_VIEW_DATA);
            }
        }

        document.addEventListener("click", handleClickOutside, true);

        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    }, [handleToggleMobileMenu, menuRef, mobileMenuOpen]);

    if (!stepViewData.length) {
        return null;
    }

    const { stepIndex } = stepViewData[stepViewData.length - 1];
    const Component = MENU_COMPONENTS[stepIndex];
    const componentProps = props[stepIndex];

    return (
        <div className={cx("mobileMenu", { mobileMenuHide: !mobileMenuOpen })}>
            <div ref={menuRef} className={cx("mobileMenuContainer")}>
                {Component && componentProps && <Component {...componentProps} />}
            </div>
            <button className={styles.burgerMenu} onClick={handleToggleMobileMenu} type="button">
                <UIIcon className={styles.burgerMenuIcon} icon="Cross" />
            </button>
        </div>
    );
};

export default HeaderMobileMenu;
